/**
 * Comp Report Container Component
 * Main container for comp report with Basic and Detailed tabs
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import {
  Assessment,
  BarChart
} from '@mui/icons-material';
import { useFilmSubscription } from '../../../hooks/useFilmSubscription';
import { FILM_TIERS } from '../../../lib/filmSubscriptionTiers';
import { fetchCompReportData } from '../../../lib/services/compReportService';
import BasicReport from './BasicReport';
import DetailedReport from './DetailedReport';
import { exportReportToPDF } from '../../../lib/utils/pdfExport';

const CompReportContainer = ({ filmId, filmTitle, compFilms = [] }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Basic, 1 = Detailed
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { filmTier, hasFilmTier } = useFilmSubscription();

  // Check subscription access
  const canAccessBasic = hasFilmTier(FILM_TIERS.PROFESSIONAL);
  const canAccessDetailed = hasFilmTier(FILM_TIERS.ENTERPRISE);

  // Fetch report data
  useEffect(() => {
    if (!filmId || compFilms.length === 0) {
      setLoading(false);
      setError(new Error('No comp films available for comparison'));
      return;
    }

    const loadReportData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchCompReportData(
          filmId,
          filmTitle,
          compFilms
        );

        setReportData(data);
      } catch (err) {
        console.error('Failed to load comp report data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, [filmId, filmTitle, compFilms]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    // Check access before switching
    if (newValue === 0 && !canAccessBasic) {
      return;
    }
    if (newValue === 1 && !canAccessDetailed) {
      return;
    }
    setActiveTab(newValue);
  };

  // Handle PDF export
  const handleExportPDF = () => {
    if (!reportData) return;

    const reportType = activeTab === 0 ? 'basic' : 'detailed';
    exportReportToPDF(reportData, filmTitle, compFilms, reportType);
  };

  // If no comps, show message
  if (compFilms.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No comp films available. Add comp films to generate a comparison report.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header with Export Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab 
                label="Basic Report" 
                icon={<Assessment />}
                iconPosition="start"
                disabled={!canAccessBasic}
              />
              <Tab 
                label="Detailed Report" 
                icon={<BarChart />}
                iconPosition="start"
                disabled={!canAccessDetailed}
              />
            </Tabs>
          </Box>
        </Box>
        {reportData && (
          <Button
            variant="contained"
            onClick={handleExportPDF}
            disabled={loading}
          >
            Export PDF
          </Button>
        )}
      </Box>

      {/* Access Denied Messages */}
      {!canAccessBasic && activeTab === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Basic Report requires Film Professional subscription
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Upgrade to Film Professional to access comparative reports by weeks before release.
          </Typography>
        </Paper>
      )}

      {!canAccessDetailed && activeTab === 1 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Detailed Report requires Film Enterprise subscription
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Upgrade to Film Enterprise to access advanced demographic breakdowns and detailed comparative analysis.
          </Typography>
        </Paper>
      )}

      {/* Report Content */}
      {activeTab === 0 && canAccessBasic && (
        <BasicReport 
          reportData={reportData}
          loading={loading}
          error={error}
        />
      )}

      {activeTab === 1 && canAccessDetailed && (
        <DetailedReport 
          primaryFilmId={filmId}
          primaryFilmTitle={filmTitle}
          compFilms={compFilms}
          reportData={reportData}
          loading={loading}
          error={error}
        />
      )}
    </Box>
  );
};

export default CompReportContainer;

